import React, { useState } from 'react';
import css from './FilterSidebar.module.css';
import FilterSectionHeader from './FilterSectionHeader';
import SearchInput from './SearchInput';
import { Button, Icons } from '../../../components';
import { FormattedMessage } from 'react-intl';
import { createResourceLocatorString } from '../../../util/routes';
import { PARAM_KEYS } from '../../../util/enums';
import { divisionSchoolOptions } from '../../../config/configExtras';
import classNames from 'classnames';

const ConferenceFilterSection = ({
  handleClearFilter,
  history,
  routeConfiguration,
  listingCategory,
  pageName = 'SearchPage',
  isBlackBgFilter,
  isTalentOfferingFilters,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConferences, setSelectedConferences] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Filter conferences based on search term (case-insensitive)
  const seen = new Set();
  const filteredConferences = divisionSchoolOptions
    .filter(s => {
      const conf = s?.conference;
      if (typeof conf !== 'string') return false;
      const lowerConf = conf.toLowerCase();
      if (!lowerConf.includes(searchTerm.toLowerCase())) return false;
      if (seen.has(lowerConf)) return false;
      seen.add(lowerConf);
      return true;
    })
    .slice(0, 10); // Limit to 10 results

  // Handle selecting a conference
  const handleSelectConference = conference => {
    if (!selectedConferences.includes(conference)) {
      setSelectedConferences([...selectedConferences, conference]);
    }
    setSearchTerm(''); // Clear input after selection
    setIsDropdownOpen(false); // Close dropdown
  };

  // Handle removing a selected conference and updating the URL
  const handleRemoveConference = conference => {
    const updatedConferences = selectedConferences.filter(c => c !== conference);
    setSelectedConferences(updatedConferences);

    // Update the URL
    const searchParams = {
      pub_listingCategory: listingCategory,
      ...(updatedConferences.length > 0 && { keywords: updatedConferences.join(',') }),
    };
    history.push(createResourceLocatorString(pageName, routeConfiguration, {}, searchParams));
  };

  // Handle search submission
  const handleSearchSubmit = () => {
    if (selectedConferences.length > 0) {
      const searchParams = {
        pub_listingCategory: listingCategory,
        keywords: selectedConferences.join(','), // Pass as comma-separated string
      };
      history.push(createResourceLocatorString(pageName, routeConfiguration, {}, searchParams));
    } else {
      console.warn('No conferences selected.');
    }
  };

  // Handle Enter key
  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredConferences.length === 1) {
        // Auto-select the only suggestion on Enter
        handleSelectConference(filteredConferences[0].conference);
      } else {
        handleSearchSubmit();
      }
    }
  };

  // Handle input focus to show dropdown
  const handleInputFocus = () => {
    if (searchTerm.length >= 1) {
      setIsDropdownOpen(true);
    }
  };

  // Handle input change
  const handleInputChange = e => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsDropdownOpen(value.length >= 1); // Show dropdown when typing
  };

  // Handle clear filter
  const handleClear = () => {
    handleClearFilter(PARAM_KEYS.conference);
    setSearchTerm('');
    setSelectedConferences([]);
    setIsDropdownOpen(false);
    // Reset URL by removing keywords
    const searchParams = {
      pub_listingCategory: listingCategory,
    };
    history.push(createResourceLocatorString(pageName, routeConfiguration, {}, searchParams));
  };

  return (
    <div
      className={classNames(
        css.filterSection,
        isTalentOfferingFilters ? css.talentOfferingFilters : null
      )}
    >
      {!isTalentOfferingFilters && (
        <FilterSectionHeader
          isBlackBgFilter={isBlackBgFilter}
          titleId="FilterCategories.conference"
          handleClear={handleClear}
        />
      )}

      {/* Selected conferences as tags */}
      {selectedConferences.length > 0 && (
        <div className={css.selectedConferences}>
          {selectedConferences.map(conf => (
            <div key={conf} className={css.selectedTag}>
              {conf}
              <span
                className={css.removeTag}
                onClick={() => handleRemoveConference(conf)}
                role="button"
                aria-label={`Remove ${conf}`}
              >
                <Icons name="cross" />
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Search input with autopopulating dropdown */}
      <div className={css.searchContainer}>
        <SearchInput
          type="text"
          className={css.searchInput}
          placeholder="Search Conference..."
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
        />
        {isDropdownOpen && filteredConferences.length > 0 && (
          <ul className={css.suggestionsDropdown}>
            {filteredConferences.map(({ conference }) => (
              <li
                key={conference}
                className={css.suggestionItem}
                onClick={() => handleSelectConference(conference)}
                role="option"
                aria-selected={selectedConferences.includes(conference)}
              >
                {conference}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Button type="button" className={css.applyButton} onClick={handleSearchSubmit}>
        <FormattedMessage id="FilterCategories.apply" />
      </Button>
    </div>
  );
};

export default ConferenceFilterSection;
